import {
  DeleteIcon,
  FormControlLabel,
  GKButton,
  GKCheckbox,
  GKSearchSelect,
  GKTextField,
  Grid2,
  IconButton,
  Paper,
  PlusIcon,
  Show,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "components";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import {
  billingFrequency,
  FeeMethod,
  FeeParameter,
  FeeType,
  // billingFrequency,
  // calculationFrequency,
  managementFeeCalculationMethod,
  SUBSCRIPTION_FREQUENCY,
} from "utils/constants";

const ManagementFee = () => {
  const form: any = useFormContext();

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "managementFee.managementFeeSlab",
  });

  form.watch(["managementFee.feeType", "managementFee.calculationBenchmark"]);

  const isFeeTypeFix = form.getValues("managementFee.feeType") !== "fixed";

  const isOpeningAUMBaseParameter = [
    "opening_aum_current_value",
    "opening_aum_portfolio_value",
  ].includes(form.getValues("managementFee.calculationBenchmark"));

  const isMonthEndPortfolioValue =
    form.getValues("managementFee.calculationBenchmark") ===
    "month_end_portfolio_value_average";

  return (
    <Grid2 container rowSpacing={2} spacing={1} size={12}>
      <Grid2 size={12}>
        <Typography fontSize={18} fontWeight={500}>
          Management Fee
        </Typography>
      </Grid2>
      <Grid2 size={12}>
        <Controller
          name="managementFee.includeDividend"
          control={form.control}
          render={({ field }) => (
            <FormControlLabel
              control={<GKCheckbox {...field} checked={field.value} />}
              label={"Include Dividend"}
            />
          )}
        />
      </Grid2>
      {/* <Controller
          name="managementFee.highwatermark"
          control={form.control}
          render={({ field }) => (
            <FormControlLabel
              control={
                <GKCheckbox
                  {...field}
                  name="managementFee.highwatermark"
                  checked={field.value}
                />
              }
              label={"High Water Mark"}
            />
          )}
        /> */}

      <Grid2 size={6}>
        <Controller
          name="managementFee.productName"
          control={form.control}
          render={({ field }) => (
            <GKTextField
              {...field}
              placeholder="Product Name"
              tooltipTitle="This name will appear on the invoices generated for your clients."
              inputLabel={"Product Name"}
            />
          )}
        />
      </Grid2>

      <Grid2 size={6}>
        <Controller
          name="managementFee.hsnCode"
          control={form.control}
          rules={{
            required: {
              value: true,
              message: "Please enter HSN code",
            },
          }}
          render={({ field, fieldState: { error } }) => (
            <GKTextField
              {...field}
              requiredField
              placeholder="HSN Code"
              inputLabel={"HSN Code"}
              tooltipTitle="This code will appear on invoices to classify your product for tax compliance."
              error={!!error}
              helperText={error?.message}
            />
          )}
        />
      </Grid2>

      <Grid2 size={4}>
        <Controller
          name="managementFee.feeType"
          control={form.control}
          rules={{
            required: {
              value: true,
              message: "Please select a Fee Type",
            },
          }}
          render={({ field }) => (
            <GKSearchSelect
              {...field}
              requiredField
              inputLabel="Fee Type"
              disableClearable={false}
              options={FeeType || []}
              onChange={(e, val) => {
                field.onChange(val?.value);
              }}
              value={
                FeeType.find((dataItem) => dataItem.value === field.value) || ""
              }
              getOptionLabel={(option) => option.name || option}
              error={Boolean(form?.formState?.errors?.managementFee?.feeType)}
              helperText={
                form?.formState?.errors?.managementFee?.feeType?.message
              }
            />
          )}
        />
      </Grid2>

      <Show.When isTrue={isFeeTypeFix}>
        <Grid2 size={4}>
          <Controller
            name="managementFee.feeMethod"
            control={form.control}
            render={({ field }) => (
              <GKSearchSelect
                {...field}
                inputLabel="Fee Method"
                disableClearable={false}
                options={FeeMethod || []}
                onChange={(e, val) => field.onChange(val?.value)}
                value={
                  FeeMethod.find(
                    (dataItem) => dataItem.value === field.value,
                  ) || ""
                }
                getOptionLabel={(option) => option.name || option}
              />
            )}
          />
        </Grid2>
        <Grid2 size={4}>
          <Controller
            name="managementFee.calculationBenchmark"
            control={form.control}
            render={({ field }) => (
              <GKSearchSelect
                {...field}
                inputLabel="Fee Parameter"
                disableClearable={false}
                options={FeeParameter}
                onChange={(e, val) => {
                  field.onChange(val?.value);

                  if (
                    [
                      "opening_aum_current_value",
                      "opening_aum_portfolio_value",
                      "month_end_portfolio_value_average",
                    ].includes(val?.value)
                  ) {
                    form.setValue(
                      "managementFee.calculationMethod",
                      "trailing",
                    );
                  }
                }}
                value={
                  FeeParameter.find(
                    (dataItem) => dataItem.value === field.value,
                  ) || ""
                }
                getOptionLabel={(option) => option.name || option}
              />
            )}
          />
        </Grid2>
      </Show.When>

      <Grid2 size={4}>
        <Controller
          name="managementFee.fee"
          control={form.control}
          rules={{
            required: {
              value: true,
              message: "Please enter a Fee",
            },
          }}
          render={({ field }) => (
            <GKTextField
              {...field}
              requiredField
              inputLabel={!isFeeTypeFix ? "Fee Amount" : "Fee %"}
              type="number"
              error={Boolean(form?.formState?.errors?.managementFee?.fee)}
              helperText={form?.formState?.errors?.managementFee?.fee?.message}
            />
          )}
        />
      </Grid2>
      <Grid2 size={4}>
        <Controller
          name="managementFee.billFrequency"
          control={form.control}
          render={({ field }) => (
            <GKSearchSelect
              {...field}
              inputLabel="Billing Frequency"
              disableClearable={false}
              options={billingFrequency || []}
              onChange={(e, val) => field.onChange(val ? val.value : null)}
              value={
                billingFrequency.find(
                  (dataItem) => dataItem.value === field.value,
                ) || ""
              }
              getOptionLabel={(option) => option.name || option}
            />
          )}
        />
      </Grid2>
      <Grid2 size={4}>
        <Controller
          name="managementFee.outOfPocketExpenses"
          control={form.control}
          render={({ field }) => (
            <GKTextField
              {...field}
              type="number"
              inputLabel="Out Of Pocket Expense"
            />
          )}
        />
      </Grid2>
      {/* <Grid2 size={4}>

          <Controller
            name="managementFee.minimumFee"
            control={form.control}
            render={({ field }) => (
              <GKTextField
                {...field}
                inputLabel="Minimum Fee"
                type="number"
              />
            )}
          />

      </Grid2>
      <Grid2 size={4}>

          <Controller
            name="managementFee.maximumFee"
            control={form.control}
            render={({ field }) => (
              <GKTextField
                {...field}
                inputLabel="Maximum Fee"
                type="number"
              />
            )}
          />

      </Grid2> */}
      {/* <Grid2 size={4}>

          <Controller
            name="managementFee.softHurdle"
            control={form.control}
            render={({ field }) => (
              <GKTextField
                {...field}
                inputLabel="Soft Hurdle"
                type="number"
              />
            )}
          />

      </Grid2>
      <Grid2 size={4}>

          <Controller
            name="managementFee.hardHurdle"
            control={form.control}
            render={({ field }) => (
              <GKTextField
                {...field}
                inputLabel="Hard Hurdle"
                type="number"
              />
            )}
          />

      </Grid2> */}

      {isFeeTypeFix &&
        !isOpeningAUMBaseParameter &&
        !isMonthEndPortfolioValue && (
          <Grid2 size={4}>
            <Controller
              name="managementFee.calculationMethod"
              control={form.control}
              rules={{
                required: {
                  value: true,
                  message: "Please select a Calculation Method",
                },
              }}
              render={({ field }) => (
                <GKSearchSelect
                  {...field}
                  requiredField
                  disabled={
                    !form.getValues("managementFee.calculationBenchmark")
                  }
                  inputLabel="Calculation Method"
                  disableClearable={false}
                  options={
                    managementFeeCalculationMethod ||
                    // form.getValues("managementFee.calculationBenchmark") !== "aum"
                    // ? managementFeeCalculationMethod.filter(
                    //     (dataItem) => dataItem.value === "trailing"
                    //   )
                    // : managementFeeCalculationMethod
                    []
                  }
                  onChange={(e, val) => field.onChange(val?.value)}
                  value={
                    managementFeeCalculationMethod.find(
                      (dataItem) => dataItem.value === field.value,
                    ) || ""
                  }
                  getOptionLabel={(option) => option.name || option}
                  error={Boolean(
                    form?.formState?.errors?.managementFee?.calculationMethod,
                  )}
                  helperText={
                    form?.formState?.errors?.managementFee?.calculationMethod
                      ?.message
                  }
                />
              )}
            />
          </Grid2>
        )}

      <Show.When
        isTrue={isOpeningAUMBaseParameter && !isMonthEndPortfolioValue}
      >
        <Grid2 size={4}>
          <Controller
            name="managementFee.minimumInvestment"
            control={form.control}
            render={({ field }) => (
              <GKTextField
                {...field}
                type="number"
                inputLabel="Minimum investment"
              />
            )}
          />
        </Grid2>
        <Grid2 size={4}>
          <Controller
            name="managementFee.availableFrequency"
            control={form.control}
            render={({ field }) => (
              <GKSearchSelect
                {...field}
                disableClearable
                inputLabel="Available Billing Frequency"
                multiple
                options={SUBSCRIPTION_FREQUENCY || []}
                onChange={(event, val) => {
                  field.onChange(
                    val.map((option: { value: number }) => option.value),
                  );
                }}
                value={
                  SUBSCRIPTION_FREQUENCY?.filter((option: { value: string }) =>
                    field?.value?.includes(option?.value),
                  ) || []
                }
                getOptionLabel={(option) => option.name || option}
              />
            )}
          />
        </Grid2>
      </Show.When>

      {/* {form.getValues("managementFee.highwatermark") && (
        <Grid2 size={4}>

            <Controller
              name="managementFee.highwatermarkAmount"
              control={form.control}
              rules={{
                required: {
                  value: form.getValues("managementFee.highwatermark") || false,
                  message:
                    "High water mark amount is required when highwatermark is active",
                },
              }}
              render={({ field }) => (
                <GKTextField
                  {...field} requiredField
                  inputLabel="High Water Mark Amount"
                  name="managementFee.highwatermarkAmount"
                  type="number"
                  error={Boolean(
                    form?.formState?.errors?.managementFee?.highwatermarkAmount
                  )}
                  helperText={
                    form?.formState?.errors?.managementFee?.highwatermarkAmount
                      ?.message
                  }
                />
              )}
            />

        </Grid2>
      )} */}
      {form.getValues("managementFee.feeMethod") === "slabs" && (
        <Grid2 size={12}>
          <TableContainer
            component={Paper}
            sx={{ marginTop: 2, minHeight: 100 }}
          >
            <Table
              sx={{ minWidth: 650 }}
              size="small"
              aria-label="a dense table"
            >
              <TableHead>
                <TableRow>
                  <TableCell
                    colSpan={2}
                    style={{
                      padding: 12,
                    }}
                  >
                    <Typography
                      fontSize={16}
                      fontWeight={700}
                      color={"var(--primary-900)"}
                    >
                      Slabs
                    </Typography>
                  </TableCell>
                  <TableCell
                    colSpan={2}
                    align="right"
                    style={{
                      padding: 12,
                    }}
                  >
                    <Typography
                      fontSize={16}
                      fontWeight={700}
                      color={"var(--primary-900)"}
                    >
                      <GKButton
                        variant="outlined"
                        onClick={() => {
                          append({
                            lowerLimit: "",
                            upperLimit: "",
                            slabFee: "",
                          });
                        }}
                        startIcon={<PlusIcon />}
                      >
                        Add Slab
                      </GKButton>
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableHead>
                <TableRow>
                  <TableCell>From</TableCell>
                  <TableCell>To</TableCell>
                  <TableCell>Management Fee&nbsp;(%)</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {fields.map((row: any, index: number) => {
                  return (
                    <TableRow
                      key={row.id}
                      sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                    >
                      <TableCell component="th" scope="row">
                        <Controller
                          name={`managementFee.managementFeeSlab[${index}].lowerLimit`}
                          control={form.control}
                          render={({ field }) => (
                            <GKTextField {...field} type="number" />
                          )}
                        />
                      </TableCell>
                      <TableCell>
                        <Controller
                          name={`managementFee.managementFeeSlab[${index}].upperLimit`}
                          control={form.control}
                          render={({ field }) => (
                            <GKTextField {...field} type="number" />
                          )}
                        />
                      </TableCell>
                      <TableCell>
                        <Controller
                          name={`managementFee.managementFeeSlab[${index}].slabFee`}
                          control={form.control}
                          render={({ field }) => (
                            <GKTextField {...field} type="number" />
                          )}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          color="error"
                          className="outline"
                          disabled={fields.length === 1}
                          onClick={() => remove(index)}
                        >
                          <DeleteIcon width={16} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid2>
      )}
    </Grid2>
  );
};

export default ManagementFee;
